/**
 * Assinaturas — e-mail signatures generated for employees.
 *
 * A BU can only generate signatures once step 2 of its setup is done; the others show a padlock
 * and a banner pointing at the BU that needs finishing, rather than failing at submit with a 422.
 *
 * Three empty states, deliberately worded apart: no BU enabled at all, an enabled BU with nothing
 * generated yet, and the normal list.
 *
 * @module Pages/Signatures
 */

import { css } from '@emotion/css';
import { GLIcon } from '@group-link-one/gl-icons';
import { UTButton, UTLoading, UTTextField } from '@group-link-one/gl-ui/utilities';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import type { JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  deleteSignature,
  getEmployeeSignatures,
  restoreSignature,
} from '../../Services/Signatures.service';
import {
  getSignatureStatusPresentation,
  splitTenantsBySignatureAccess,
} from '../../Utils/signatureAccess';
import { usePrivileges } from '@/Hooks/usePrivileges';
import { useActionToast } from '@/Hooks/useActionToast';
import { useDebouncedValue } from '@/Hooks/useDebouncedValue';
import { useTenants } from '@/Hooks/useTenants';
import { compareTenantSlugs, getTenantColor, getTenantIcon } from '@/Shared/brand';
import { EmptyState } from '@/Shared/components/EmptyState/EmptyState';
import { ConfirmationDialog } from '@/Shared/components/ConfirmationDialog/ConfirmationDialog';
import { IconButton } from '@/Shared/components/IconButton/IconButton';
import { Pagination } from '@/Shared/components/Pagination/Pagination';
import { Tabs } from '@/Shared/components/Tabs/Tabs';
import { getInitials } from '@/Shared/components/PageHeader/PageHeader';
import { StatusTag } from '@/Shared/components/StatusTag/StatusTag';
import { MainContent } from '@/Templates/Main/MainContent/MainContent';
import { SignatureViewModal } from '../SignatureViewModal/SignatureViewModal';
import type { EmployeeSignature } from './Signatures.types';

const screenStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-base);
`;

const toolbarStyle = css`
  display: flex;
  align-items: flex-start;
  gap: var(--space-base);
  flex-wrap: wrap;
`;

const searchStyle = css`
  /* Wide enough for a full name, but it yields the row first when the toolbar wraps. */
  flex: 0 1 280px;
  min-width: 200px;
`;

const descriptionStyle = css`
  margin: 0;
  max-width: 620px;
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  text-wrap: pretty;
`;

const spacerStyle = css`
  flex: 1;
`;

const chipsRowStyle = css`
  display: flex;
  align-items: center;
  gap: var(--space-s);
  flex-wrap: wrap;
`;

/*
  Sticky because it carries the page controls: paging from the bottom of a grid of cards otherwise
  means scrolling back up first. The tab bar already draws the bottom rule, so the pagination rides
  on it instead of taking a row of its own.
*/
const tabsRowStyle = css`
  position: sticky;
  top: 0;
  z-index: 2;
  display: flex;
  align-items: flex-end;
  gap: var(--space-base);
  padding-top: var(--space-s);
  background: var(--color-bg);
  border-bottom: 1px solid var(--color-border);

  > *:first-child {
    border-bottom: none;
    margin-bottom: -1px;
  }
`;

const chipsLabelStyle = css`
  font-size: var(--font-size-2xs);
  font-weight: var(--font-weight-bold);
  letter-spacing: var(--letter-spacing-caps);
  text-transform: uppercase;
  color: var(--color-text-muted);
  margin-right: 4px;
`;

const chipStyle = css`
  display: flex;
  align-items: center;
  gap: 7px;
  height: 34px;
  padding: 0 13px;
  border: 1px solid var(--color-border-strong);
  border-radius: var(--radius-round);
  background: var(--color-surface);
  cursor: pointer;
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-secondary);
`;

const chipSelectedStyle = css`
  border-color: var(--color-primary);
  background: var(--color-primary-subtle);
  color: var(--color-primary);
`;

/**
 * A blocked BU has to read as blocked at a glance, not on hover: a dashed border, a struck-out
 * label and reduced opacity, on top of the padlock. Only tinting the text is far too subtle.
 */
const chipLockedStyle = css`
  cursor: not-allowed;
  color: var(--color-text-muted);
  background: var(--color-surface-sunken);
  border-style: dashed;
  border-color: var(--color-border-strong);
  opacity: 0.7;

  > span {
    text-decoration: line-through;
    text-decoration-color: var(--color-text-muted);
  }
`;

const lockedHintStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
  font-style: italic;
`;

const bannerStyle = css`
  display: flex;
  align-items: flex-start;
  gap: var(--space-sm);
  padding: var(--space-m) var(--space-ml);
  border: 1px solid var(--color-border);
  border-left: 3px solid var(--color-warning);
  border-radius: var(--radius-m);
  background: var(--color-warning-subtle);
`;

const bannerTextStyle = css`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const bannerTitleStyle = css`
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
`;

const bannerBodyStyle = css`
  font-size: var(--font-size-xs);
  line-height: var(--line-height-normal);
  color: var(--color-text-secondary);
  text-wrap: pretty;
`;

const gridStyle = css`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(340px, 100%), 1fr));
  gap: var(--space-base);
`;

const cardStyle = css`
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-card);
  box-shadow: var(--shadow-xs);
  padding: var(--space-ml);
  display: flex;
  flex-direction: column;
  gap: var(--space-m);
`;

const cardHeaderStyle = css`
  display: flex;
  align-items: center;
  gap: var(--space-sm);
`;

const avatarStyle = css`
  width: 40px;
  height: 40px;
  flex: 0 0 auto;
  border-radius: var(--radius-round);
  background: var(--color-primary-subtle);
  color: var(--color-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
`;

const identityStyle = css`
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
`;

const nameStyle = css`
  font-size: 14px;
  font-weight: var(--font-weight-semibold);
`;

const positionStyle = css`
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
`;

const cardActionsStyle = css`
  display: flex;
  align-items: center;
  gap: var(--space-s);
  margin-top: auto;
`;

const previewStyle = css`
  border: 1px solid var(--color-border);
  border-left: 3px solid var(--gl-blue);
  border-radius: var(--radius-m);
  padding: var(--space-m);
  display: flex;
  flex-direction: column;
  gap: 3px;
  background: var(--color-surface-sunken);
`;

const previewNameStyle = css`
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
`;

const previewRoleStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-text-secondary);
`;

const previewEmailStyle = css`
  font-family: var(--font-mono);
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
`;

const previewPhoneStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
`;

/** Four rows of the three-column grid. */
const SIGNATURES_PER_PAGE = 12;

type TabId = 'live' | 'deleted';

const Signatures = (): JSX.Element => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useActionToast();
  const { pages: privileges } = usePrivileges();
  const [viewing, setViewing] = useState<EmployeeSignature | undefined>();
  const { tenants, isLoading: isLoadingTenants, error: tenantsError } = useTenants();

  const [activeTenant, setActiveTenant] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [tab, setTab] = useState<TabId>('live');
  const [deleting, setDeleting] = useState<EmployeeSignature | undefined>();

  /*
    Debounced because the filter runs on the server: the BFF matches name OR e-mail, partially and
    case-insensitively, over the whole BU. Filtering here instead would only search the 100 items
    already loaded and quietly miss the rest.
  */
  const debouncedSearch = useDebouncedValue(search);

  const { enabled, blocked } = useMemo(() => splitTenantsBySignatureAccess(tenants), [tenants]);

  const orderedEnabled = useMemo(
    () => [...enabled].sort((a, b) => compareTenantSlugs(a.slug, b.slug)),
    [enabled],
  );

  // Select the first available BU once the list arrives.
  useEffect(() => {
    if (activeTenant === null && orderedEnabled.length > 0) {
      setActiveTenant(orderedEnabled[0].slug);
    }
  }, [activeTenant, orderedEnabled]);

  /*
    Paged on the server, unlike the users screen: this endpoint really pages, and a BU can hold far
    more signatures than a screenful. Asking for everything and slicing here would grow into a
    bigger and bigger response for a page that shows twelve cards.
  */
  const { data, isLoading } = useQuery({
    queryKey: ['signatures', activeTenant, debouncedSearch, tab, page],
    queryFn: () =>
      getEmployeeSignatures({
        tenantSlug: activeTenant ?? undefined,
        ...(debouncedSearch.trim() ? { search: debouncedSearch.trim() } : {}),
        ...(tab === 'deleted' ? { includeDeleted: true } : {}),
        page,
        limit: SIGNATURES_PER_PAGE,
      }),
    enabled: activeTenant !== null,
    // Keeps the current results on screen while the next search resolves, instead of flashing
    // the empty state between keystrokes.
    placeholderData: previous => previous,
  });

  const removeSignature = useMutation({
    mutationFn: deleteSignature,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['signatures'] });
      toast.success('Assinatura excluída');
      setDeleting(undefined);
    },
    onError: (error: Error) => {
      toast.failure('Não foi possível excluir a assinatura', error);
    },
  });

  const bringBack = useMutation({
    mutationFn: restoreSignature,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['signatures'] });
      toast.success('Assinatura restaurada');
    },
    onError: (error: Error) => {
      toast.failure('Não foi possível restaurar a assinatura', error);
    },
  });

  /*
    The listing only reports the total of the tab being shown, so the other badge needs its own
    count — asked for with `limit: 1`, since only `pagination.total` is read from it.
  */
  const { data: otherTab } = useQuery({
    queryKey: ['signatures', 'count', activeTenant, debouncedSearch, tab === 'live'],
    queryFn: () =>
      getEmployeeSignatures({
        tenantSlug: activeTenant ?? undefined,
        ...(debouncedSearch.trim() ? { search: debouncedSearch.trim() } : {}),
        ...(tab === 'live' ? { includeDeleted: true } : {}),
        limit: 1,
      }),
    enabled: activeTenant !== null,
  });

  const signatures = useMemo(() => data?.data ?? [], [data]);
  const total = data?.pagination?.total ?? signatures.length;
  const otherTotal = otherTab?.pagination?.total;

  // Undefined while a count is still unknown: the Tabs badge renders 0 but skips undefined, and a
  // 0 that turns into 14 a moment later is worse than no badge at all.
  const liveCount = tab === 'live' ? total : otherTotal;
  const deletedCount = tab === 'deleted' ? total : otherTotal;

  // A new search or another BU starts over: page 4 of the old result means nothing in the new one.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, activeTenant, tab]);
  const activeTenantName =
    orderedEnabled.find(tenant => tenant.slug === activeTenant)?.name ?? activeTenant;

  if (isLoadingTenants) {
    return (
      <MainContent id='signatures-main' styling={{ noBackgroundColor: true, noBorder: true }}>
        <UTLoading type='dots' />
      </MainContent>
    );
  }

  const hasEnabledTenants = orderedEnabled.length > 0;

  return (
    <MainContent id='signatures-main' styling={{ noBackgroundColor: true, noBorder: true }}>
      <div className={screenStyle}>
        <div className={toolbarStyle}>
          <p className={descriptionStyle}>
            Assinaturas de e-mail geradas para funcionários. Só BUs com o passo de assinatura
            concluído podem gerar assinatura.
          </p>
          <div className={spacerStyle} />
          {hasEnabledTenants ? (
            <div className={searchStyle}>
              <UTTextField
                id='signatures-search'
                placeholder='Buscar por nome ou e-mail'
                value={search}
                leftIcon='Misc.MagnifyingGlass'
                onChange={event => {
                  setSearch(event.target.value);
                }}
              />
            </div>
          ) : null}
          {privileges.SIGNATURES.CREATE && hasEnabledTenants ? (
            <UTButton
              fitContent
              id='signatures-create'
              text='Criar assinatura'
              icon='Arrows.Plus'
              onClick={() => {
                navigate('/signatures/create');
              }}
            />
          ) : null}
        </div>

        {hasEnabledTenants ? (
          <div className={chipsRowStyle}>
            <span className={chipsLabelStyle}>Business unit</span>
            {orderedEnabled.map(tenant => (
              <button
                key={tenant.slug}
                type='button'
                className={`${chipStyle} ${activeTenant === tenant.slug ? chipSelectedStyle : ''}`}
                onClick={() => {
                  setActiveTenant(tenant.slug);
                }}
              >
                <GLIcon
                  variant={getTenantIcon(tenant)}
                  size={15}
                  fill={getTenantColor(tenant.slug)}
                />
                <span>{tenant.name || tenant.slug}</span>
              </button>
            ))}
            {blocked.map(tenant => (
              <button
                key={tenant.slug}
                type='button'
                disabled
                title={`${tenant.name || tenant.slug} ainda não concluiu o passo de assinatura`}
                className={`${chipStyle} ${chipLockedStyle}`}
              >
                <GLIcon variant={getTenantIcon(tenant)} size={15} fill='var(--color-text-muted)' />
                <span>{tenant.name || tenant.slug}</span>
                <GLIcon variant='Misc.Lock' size={15} fill='var(--color-text-muted)' />
              </button>
            ))}
            {blocked.length > 0 ? (
              <span className={lockedHintStyle}>sem assinatura configurada</span>
            ) : null}
          </div>
        ) : null}

        {blocked.length > 0 ? (
          <div className={bannerStyle}>
            <GLIcon variant='Notifications.Warning' size={18} fill='var(--gl-yellow-dark)' />
            <div className={bannerTextStyle}>
              <span className={bannerTitleStyle}>
                {blocked.length === 1
                  ? '1 business unit não gera assinatura'
                  : `${blocked.length} business units não geram assinatura`}
              </span>
              <span className={bannerBodyStyle}>
                {blocked.map(tenant => tenant.name || tenant.slug).join(', ')} —{' '}
                {blocked.length === 1 ? 'falta concluir' : 'falta concluírem'} o passo de assinatura
                no cadastro da BU.
              </span>
            </div>
            <div className={spacerStyle} />
            {privileges.BUSINESS_UNITS.PAGE ? (
              <UTButton
                fitContent
                variant='tertiary'
                size='small'
                text='Gerenciar BUs'
                onClick={() => {
                  navigate('/business-units');
                }}
              />
            ) : null}
          </div>
        ) : null}

        {tenantsError ? (
          /*
            The BU listing failed, which is not the same as there being no BU — saying "nenhuma BU
            configurada" here sends the person to configure something that may already be
            configured. The most common cause is the role not holding `tenant:read`.
          */
          <EmptyState
            id='signatures-tenants-error'
            icon='Notifications.WarningCircle'
            title='Não foi possível carregar as BUs'
            description={tenantsError}
          />
        ) : !hasEnabledTenants ? (
          <EmptyState
            id='signatures-no-bu'
            icon='Misc.Lock'
            title='Nenhuma BU com assinatura configurada'
            description='Conclua o passo de assinatura em pelo menos uma business unit para gerar assinaturas aqui.'
            action={
              privileges.BUSINESS_UNITS.PAGE ? (
                <UTButton
                  fitContent
                  text='Gerenciar BUs'
                  onClick={() => {
                    navigate('/business-units');
                  }}
                />
              ) : undefined
            }
          />
        ) : null}

        {hasEnabledTenants ? (
          <div className={tabsRowStyle}>
            <Tabs
              activeId={tab}
              items={[
                { id: 'live', label: 'Entregues', badge: liveCount },
                { id: 'deleted', label: 'Excluídas', badge: deletedCount },
              ]}
              onChange={id => {
                setTab(id as TabId);
              }}
            />
            <div className={spacerStyle} />
            <Pagination
              id='signatures'
              page={page}
              pageSize={SIGNATURES_PER_PAGE}
              total={total}
              onChange={setPage}
            />
          </div>
        ) : null}

        {hasEnabledTenants && isLoading ? <UTLoading type='dots' /> : null}

        {hasEnabledTenants && !isLoading && signatures.length === 0 ? (
          <EmptyState
            id='signatures-none-in-bu'
            icon={debouncedSearch.trim() ? 'Misc.MagnifyingGlass' : 'Communication.Envelope'}
            title={
              debouncedSearch.trim()
                ? `Ninguém encontrado para "${debouncedSearch.trim()}"`
                : `Nenhuma assinatura gerada em ${activeTenantName}`
            }
            /*
              A search that finds nothing is not an empty BU: offering "criar a primeira
              assinatura" there would be answering a question nobody asked.
            */
            description={
              debouncedSearch.trim()
                ? `A busca cobre nome e e-mail em ${activeTenantName}.`
                : 'O passo de assinatura está concluído nesta BU. Crie a primeira assinatura para os funcionários dela.'
            }
            action={
              privileges.SIGNATURES.CREATE && !debouncedSearch.trim() ? (
                <UTButton
                  fitContent
                  text='Criar assinatura'
                  icon='Arrows.Plus'
                  onClick={() => {
                    navigate('/signatures/create');
                  }}
                />
              ) : undefined
            }
          />
        ) : null}

        {signatures.length > 0 ? (
          <div className={gridStyle}>
            {signatures.map(signature => {
              const presentation = getSignatureStatusPresentation(signature.status);

              return (
                <article
                  key={signature.id}
                  id={`signature-card-${signature.id}`}
                  className={cardStyle}
                >
                  <div className={cardHeaderStyle}>
                    <span className={avatarStyle}>{getInitials(signature.employeeName)}</span>
                    <div className={identityStyle}>
                      <span className={nameStyle}>{signature.employeeName}</span>
                      {signature.position ? (
                        <span className={positionStyle}>{signature.position}</span>
                      ) : null}
                    </div>
                    <div className={spacerStyle} />
                    <StatusTag status={presentation.status} size='sm'>
                      {presentation.label}
                    </StatusTag>
                  </div>

                  <div className={previewStyle}>
                    <span className={previewNameStyle}>{signature.employeeName}</span>
                    <span className={previewRoleStyle}>
                      {signature.position ? `${signature.position} · ` : ''}GroupLink One
                    </span>
                    <span className={previewEmailStyle}>{signature.email}</span>
                    {signature.phone ? (
                      <span className={previewPhoneStyle}>{signature.phone}</span>
                    ) : null}
                  </div>

                  <div className={cardActionsStyle}>
                    <UTButton
                      fitContent
                      id={`signature-view-button-${signature.id}`}
                      variant='secondary'
                      size='small'
                      text='Visualizar'
                      icon='Misc.Eye'
                      onClick={() => {
                        setViewing(signature);
                      }}
                    />
                    {privileges.SIGNATURES.DOWNLOAD ? (
                      <UTButton
                        fitContent
                        id={`signature-download-button-${signature.id}`}
                        variant='secondary'
                        size='small'
                        text='Baixar'
                        icon='Arrows.TrayArrowUp'
                        onClick={() => {
                          setViewing(signature);
                        }}
                      />
                    ) : null}

                    <div className={spacerStyle} />

                    {/*
                      Gated by the same flag as creating: whoever can add a signature can retire
                      it. The BFF guards it with `signature:send` for the same reason.
                    */}
                    {privileges.SIGNATURES.CREATE && tab === 'live' ? (
                      <IconButton
                        id={`signature-delete-${signature.id}`}
                        icon='Misc.Trash'
                        size='sm'
                        aria-label={`Excluir a assinatura de ${signature.employeeName}`}
                        onClick={() => {
                          setDeleting(signature);
                        }}
                      />
                    ) : null}

                    {privileges.SIGNATURES.CREATE && tab === 'deleted' ? (
                      <UTButton
                        fitContent
                        variant='secondary'
                        size='small'
                        text='Restaurar'
                        icon='Arrows.ArrowCounterClockwise'
                        disabled={bringBack.isPending}
                        onClick={() => {
                          bringBack.mutate(signature.id);
                        }}
                      />
                    ) : null}
                  </div>
                </article>
              );
            })}
          </div>
        ) : null}
      </div>

      {deleting ? (
        <ConfirmationDialog
          open
          title='Excluir assinatura'
          variant='danger'
          message={`Excluir a assinatura de ${deleting.employeeName}? Ela sai desta lista e passa para a aba Excluídas, de onde pode voltar. O registro do e-mail já enviado é mantido.`}
          confirmText={removeSignature.isPending ? 'Excluindo...' : 'Excluir'}
          isLoading={removeSignature.isPending}
          onConfirm={() => {
            removeSignature.mutate(deleting.id);
          }}
          onClose={() => {
            setDeleting(undefined);
          }}
        />
      ) : null}

      {/* UTModal keeps its content in the a11y tree when closed, so mount it only when needed. */}
      {viewing ? (
        <SignatureViewModal
          open
          signature={viewing}
          /* Only for a live signature: a deleted one is not something to send anybody. */
          canResend={privileges.SIGNATURES.CREATE && tab === 'live'}
          onResent={() => {
            void queryClient.invalidateQueries({ queryKey: ['signatures'] });
          }}
          canDownload={privileges.SIGNATURES.DOWNLOAD}
          onClose={() => {
            setViewing(undefined);
          }}
        />
      ) : null}
    </MainContent>
  );
};

export { Signatures };
